import { StrictMode, useEffect, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider, useAuth } from "@clerk/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router";
import { Toaster } from "@/components/ui/sonner";
import { router } from "@/app/router";
import { ThemeProvider } from "@/lib/theme";
import { ApiError, setAuthTokenGetter } from "@/lib/api";
import "@/index.css";
// Remember where this visitor came from. Loaded on its own, so it costs the first paint nothing;
// the query string is still on the page when it runs.
void import("@/lib/acquisition").then((m) => m.rememberLanding());

// vite.config.ts inlines only this one name, and types it as `any`. Read it as a string.
const clientEnv = import.meta.env as unknown as Record<string, string | undefined>;
const publishableKey = clientEnv.VITE_CLERK_PUBLISHABLE_KEY ?? "";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Queries are GETs, so a retry is safe. Never retry a 4xx: the answer will not change.
      retry: (failureCount, error) =>
        error instanceof ApiError ? error.retryable && failureCount < 2 : failureCount < 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    // Writes are never retried on their own. The screen decides, with an Idempotency-Key.
    mutations: { retry: 0 },
  },
});

/** Gives the fetch wrapper the current Clerk token. */
function AuthTokenBridge({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();
  useEffect(() => {
    setAuthTokenGetter(() => getToken());
    return () => setAuthTokenGetter(null);
  }, [getToken]);
  return children;
}

function SetupNeeded() {
  return (
    <main className="mx-auto max-w-[40rem] p-8">
      <h1 className="text-2xl font-semibold">HourSign needs one setting</h1>
      <p className="text-muted-foreground mt-3">
        The sign-in key is missing, so the app cannot start.
      </p>
      <p className="mt-3">
        Add <code className="font-mono">CLERK_PUBLISHABLE_KEY</code> to{" "}
        <code className="font-mono">app/.env</code>, then restart the dev server.
      </p>
    </main>
  );
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error('index.html is missing <div id="root">.');

createRoot(rootElement).render(
  <StrictMode>
    {publishableKey ? (
      <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/sign-in">
        <QueryClientProvider client={queryClient}>
          <AuthTokenBridge>
            <ThemeProvider>
              <RouterProvider router={router} />
              <Toaster />
            </ThemeProvider>
          </AuthTokenBridge>
        </QueryClientProvider>
      </ClerkProvider>
    ) : (
      <SetupNeeded />
    )}
  </StrictMode>,
);
